import React from 'react';
import { useHistory } from 'react-router-dom';
import { NAV_ITEMS } from '@/constants';
import {
  getParentModuleFromPlaceSpecificURL,
  usePlaceSpecificUrlFactory,
} from '@/utils/place-detection';
import styled from 'styled-components';

const StyledNavigationItem = styled.button`
  font-size: 16px;
  font-weight: 600;
  background-color: transparent;
  cursor: pointer;
  color: #101938;
  text-decoration: underline;
  margin-right: 15px;
  border-radius: 3px;
  transition:
    background-color 0.2s ease-in,
    transform 0.1s linear;
  ::before {
    content: '${(props) => props.buttonLabel}';
  }
  :hover {
    /* TODO: Get this from constant */
    background-color: #37a3bc;
  }
  :active {
    transform: scale(0.9);
  }
`;

// defines click handlers for each route nav
const createNavActions = (history, placeSpecificUrlFactory) => ({
  Map: () => history.push(placeSpecificUrlFactory(`/${NAV_ITEMS.home}`)),
  List: () => history.push(placeSpecificUrlFactory(`/${NAV_ITEMS.list}`)),
  Dashboard: () =>
    history.push(placeSpecificUrlFactory(`/${NAV_ITEMS.dashboard}`)),
  Back: () =>
    history.push(
      placeSpecificUrlFactory(
        getParentModuleFromPlaceSpecificURL(history.location.pathname)
      )
    ),
  Refresh: () => {
    history.push('/');
    window.location.reload();
  },
});

// Presentational component which is an underlined button
const NavOption = ({ onClick, buttonLabel }) => {
  if (!onClick || !buttonLabel) return null;
  return (
    <StyledNavigationItem
      data-cy="nav-option"
      buttonLabel={buttonLabel}
      onClick={() => onClick()}
    />
  );
};

/**
 * Checks if optionalNavCb is provided and calls it before performing navigation.
 * This is typically used to cleanup error state
 * @param {*} optionalCB CB to execute before nav
 * @param {*} navAction CB for completing a navigation
 */
const includeOptionalCbOnClick = (optionalCB, navAction) => {
  if (optionalCB) {
    return () => {
      optionalCB();
      navAction();
    };
  }
  return navAction;
};

/**
 * Constructs navigation buttons for placeholder when an array of options is passed
 *
 * @param {*} { selectedOptions, optionalCB }
 * @returns
 */
function NavigationOptions({ selectedOptions, optionalCB }) {
  const history = useHistory();
  const placeSpecificUrlFactory = usePlaceSpecificUrlFactory();
  const navActions = createNavActions(history, placeSpecificUrlFactory);
  return (
    <div css="display: flex; flex-direction: row; ">
      {selectedOptions.map((option) => {
        const navAction = navActions[option];
        if (!navAction) throw new Error('unsupported nav option provided');
        return (
          <NavOption
            key={option}
            onClick={includeOptionalCbOnClick(optionalCB, navAction)}
            buttonLabel={option}
          />
        );
      })}
    </div>
  );
}

/**
 * Parent component responsible for either constructing default placeholder nav buttons
 * if an array is passed or returning custom renderNav component if provided.
 *
 * @export
 * @param {*} props
 * @returns
 */
export function PlaceholderNavigationItems(props) {
  const { renderNavigationOptions, optionalCB } = props;
  if (Array.isArray(renderNavigationOptions)) {
    return (
      <NavigationOptions
        selectedOptions={renderNavigationOptions}
        optionalCB={optionalCB}
      />
    );
  }
  if (!renderNavigationOptions) return null;
  return renderNavigationOptions;
}
